/**
 * Play Archive query: unit, conflicts-only, search, per-facet filters and the
 * sort — held in the URL and applied CLIENT-SIDE over the whole event.
 *
 * The archive loads every play of the event at once (the largest real game is
 * ~110 plays), so sort and filter work on ANY column instantly without a
 * server round trip, and the URL stays the single source of truth: a refresh,
 * a bookmark or the back button restores the exact view.
 *
 * URL shape (defaults omitted so a pristine archive has a clean URL):
 *   unit=defense  conflicts=true  search=blitz
 *   sort=gain_loss&dir=desc
 *   f.down=3,4          enum facet (values OR-ed; `__blank__` = not recorded)
 *   f.gain_loss=5..     range facet (`min..max`, either side optional)
 *   f.notes=screen      text facet (contains, case-insensitive)
 * Different facets are AND-ed.
 */
import type { PlayUnitFilter, PlayView } from '../../lib/api-types';
import { readBoolParam, readEnumParam } from '../../lib/url-state';

import {
  BLANK_LABEL,
  BLANK_VALUE,
  phaseOf,
  type ArchiveColumn,
  type FilterFacet,
  type ValueOption,
} from './archive-columns';

export const ARCHIVE_PARAMS = {
  unit: 'unit',
  conflicts: 'conflicts',
  search: 'search',
  sort: 'sort',
  dir: 'dir',
  facetPrefix: 'f.',
} as const;

export const UNIT_OPTIONS: readonly { value: PlayUnitFilter; label: string; short: string }[] = [
  { value: 'all', label: 'All plays', short: 'All' },
  { value: 'offense', label: 'Offense', short: 'O' },
  { value: 'defense', label: 'Defense', short: 'D' },
  { value: 'special_teams', label: 'Special teams', short: 'ST' },
];

const UNIT_VALUES = UNIT_OPTIONS.map((option) => option.value);

export type FacetFilter =
  | { kind: 'enum'; values: readonly string[] }
  | { kind: 'range'; min: number | null; max: number | null }
  | { kind: 'text'; text: string };

export type SortDir = 'asc' | 'desc';

export interface ArchiveQuery {
  unit: PlayUnitFilter;
  isConflictsOnly: boolean;
  search: string;
  /** Facet id → filter. Absent = not filtered. */
  filters: Readonly<Record<string, FacetFilter>>;
  /** Column id, or null for the natural order (play # ascending). */
  sort: string | null;
  dir: SortDir;
}

export const EMPTY_QUERY: ArchiveQuery = {
  unit: 'all',
  isConflictsOnly: false,
  search: '',
  filters: {},
  sort: null,
  dir: 'asc',
};

/* -------------------------------------------------------------------- URL */

const MAX_FILTER_TEXT = 80;

function parseRange(raw: string): FacetFilter | null {
  const match = /^\s*(-?\d+)?\s*\.\.\s*(-?\d+)?\s*$/.exec(raw);
  if (match === null) return null;
  const min = match[1] === undefined ? null : Number(match[1]);
  const max = match[2] === undefined ? null : Number(match[2]);
  if (min === null && max === null) return null;
  return { kind: 'range', min, max };
}

/** Read the query, keeping only facets the registry knows (never throws). */
export function readArchiveQuery(
  params: URLSearchParams,
  facets: ReadonlyMap<string, { facet: FilterFacet }>,
  columnIds: ReadonlySet<string>
): ArchiveQuery {
  const filters: Record<string, FacetFilter> = {};
  for (const [key, raw] of params.entries()) {
    if (!key.startsWith(ARCHIVE_PARAMS.facetPrefix)) continue;
    const id = key.slice(ARCHIVE_PARAMS.facetPrefix.length);
    const entry = facets.get(id);
    if (entry === undefined || raw.trim() === '') continue;
    if (entry.facet.kind === 'enum') {
      const values = [
        ...new Set(
          raw
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value !== '' && value.length <= MAX_FILTER_TEXT)
        ),
      ];
      if (values.length > 0) filters[id] = { kind: 'enum', values };
    } else if (entry.facet.kind === 'range') {
      const range = parseRange(raw);
      if (range !== null) filters[id] = range;
    } else {
      filters[id] = { kind: 'text', text: raw.slice(0, MAX_FILTER_TEXT) };
    }
  }
  const sortRaw = params.get(ARCHIVE_PARAMS.sort);
  const sort = sortRaw !== null && columnIds.has(sortRaw) ? sortRaw : null;
  return {
    unit: readEnumParam(params, ARCHIVE_PARAMS.unit, UNIT_VALUES, 'all'),
    isConflictsOnly: readBoolParam(params, ARCHIVE_PARAMS.conflicts),
    search: params.get(ARCHIVE_PARAMS.search) ?? '',
    filters,
    sort,
    dir:
      sort === null
        ? 'asc'
        : readEnumParam(params, ARCHIVE_PARAMS.dir, ['asc', 'desc'] as const, 'asc'),
  };
}

function encodeFilter(filter: FacetFilter): string | null {
  if (filter.kind === 'enum') return filter.values.length === 0 ? null : filter.values.join(',');
  if (filter.kind === 'range') {
    if (filter.min === null && filter.max === null) return null;
    return `${filter.min ?? ''}..${filter.max ?? ''}`;
  }
  return filter.text.trim() === '' ? null : filter.text;
}

/** Serialise, dropping defaults and preserving params the page does not own. */
export function writeArchiveQuery(query: ArchiveQuery, existing: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(existing);
  for (const key of [...next.keys()]) {
    if (key.startsWith(ARCHIVE_PARAMS.facetPrefix)) next.delete(key);
  }
  // Retired parameters from the paged archive.
  for (const key of ['page', 'limit', 'conflict_status', 'q']) next.delete(key);
  const set = (key: string, value: string | null): void => {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  };
  set(ARCHIVE_PARAMS.unit, query.unit === 'all' ? null : query.unit);
  set(ARCHIVE_PARAMS.conflicts, query.isConflictsOnly ? 'true' : null);
  set(ARCHIVE_PARAMS.search, query.search.trim() === '' ? null : query.search);
  set(ARCHIVE_PARAMS.sort, query.sort);
  set(ARCHIVE_PARAMS.dir, query.sort !== null && query.dir === 'desc' ? 'desc' : null);
  for (const [id, filter] of Object.entries(query.filters)) {
    const encoded = encodeFilter(filter);
    if (encoded !== null) next.set(`${ARCHIVE_PARAMS.facetPrefix}${id}`, encoded);
  }
  return next;
}

/* ------------------------------------------------------------- mutations */

export function withFilter(
  query: ArchiveQuery,
  id: string,
  filter: FacetFilter | null
): ArchiveQuery {
  const filters = { ...query.filters };
  if (filter === null || encodeFilter(filter) === null) delete filters[id];
  else filters[id] = filter;
  return { ...query, filters };
}

export function toggleEnumValue(query: ArchiveQuery, id: string, value: string): ArchiveQuery {
  const current = query.filters[id];
  const values = current?.kind === 'enum' ? current.values : [];
  const next = values.includes(value)
    ? values.filter((entry) => entry !== value)
    : [...values, value];
  return withFilter(query, id, { kind: 'enum', values: next });
}

/** asc → desc → none. */
export function cycleSort(query: ArchiveQuery, columnId: string): ArchiveQuery {
  if (query.sort !== columnId) return { ...query, sort: columnId, dir: 'asc' };
  if (query.dir === 'asc') return { ...query, dir: 'desc' };
  return { ...query, sort: null, dir: 'asc' };
}

export function clearAll(query: ArchiveQuery): ArchiveQuery {
  return { ...query, unit: 'all', isConflictsOnly: false, search: '', filters: {} };
}

export function hasActiveFilters(query: ArchiveQuery): boolean {
  return (
    query.unit !== 'all' ||
    query.isConflictsOnly ||
    query.search.trim() !== '' ||
    Object.keys(query.filters).length > 0
  );
}

/* ---------------------------------------------------------------- chips */

export interface ArchiveChip {
  id: string;
  label: string;
  clear: (query: ArchiveQuery) => ArchiveQuery;
}

export function describeFilter(facet: FilterFacet, filter: FacetFilter): string {
  if (filter.kind === 'enum') {
    const label = facet.optionLabel ?? ((value: string) => value);
    return filter.values
      .map((value) => (value === BLANK_VALUE ? BLANK_LABEL : label(value)))
      .join(' or ');
  }
  if (filter.kind === 'range') {
    if (filter.min !== null && filter.max !== null) {
      return filter.min === filter.max ? `= ${filter.min}` : `${filter.min} to ${filter.max}`;
    }
    return filter.min !== null ? `≥ ${filter.min}` : `≤ ${filter.max ?? ''}`;
  }
  return `contains “${filter.text}”`;
}

/** One dismissible chip per applied facet (plus unit / conflicts / search). */
export function archiveChips(
  query: ArchiveQuery,
  facets: ReadonlyMap<string, { facet: FilterFacet }>
): ArchiveChip[] {
  const chips: ArchiveChip[] = [];
  if (query.isConflictsOnly) {
    chips.push({
      id: 'conflicts',
      label: 'Conflicts only',
      clear: (q) => ({ ...q, isConflictsOnly: false }),
    });
  }
  if (query.search.trim() !== '') {
    chips.push({
      id: 'search',
      label: `Search: ${query.search.trim()}`,
      clear: (q) => ({ ...q, search: '' }),
    });
  }
  for (const [id, filter] of Object.entries(query.filters)) {
    const entry = facets.get(id);
    if (entry === undefined) continue;
    chips.push({
      id: `f.${id}`,
      label: `${entry.facet.label}: ${describeFilter(entry.facet, filter)}`,
      clear: (q) => withFilter(q, id, null),
    });
  }
  return chips;
}

/* ------------------------------------------------------------- evaluation */

function matchesFacet(play: PlayView, facet: FilterFacet, filter: FacetFilter): boolean {
  if (filter.kind === 'enum') {
    const values = facet.values?.(play) ?? [];
    const own = values.length === 0 ? [BLANK_VALUE] : values.map((value) => value.toLowerCase());
    return filter.values.some((wanted) =>
      own.includes(wanted === BLANK_VALUE ? wanted : wanted.toLowerCase())
    );
  }
  if (filter.kind === 'range') {
    const value = facet.number?.(play) ?? null;
    if (value === null) return false;
    if (filter.min !== null && value < filter.min) return false;
    if (filter.max !== null && value > filter.max) return false;
    return true;
  }
  const needle = filter.text.trim().toLowerCase();
  return needle === '' || (facet.text?.(play) ?? '').toLowerCase().includes(needle);
}

/** Everything a coach might type into the search box, for one play. */
function searchText(play: PlayView, columns: readonly ArchiveColumn[]): string {
  return columns
    .map((column) => column.text(play))
    .concat(String(play.stats.play_name ?? ''), String(play.stats.notes ?? ''))
    .join(' \u0001 ')
    .toLowerCase();
}

/**
 * Apply every filter EXCEPT `skipFacetId` — used to count a facet's values
 * against the rest of the query (so the checklist says how many plays each
 * value would show).
 */
export function filterPlays(
  plays: readonly PlayView[],
  query: ArchiveQuery,
  columns: readonly ArchiveColumn[],
  facets: ReadonlyMap<string, { facet: FilterFacet }>,
  skipFacetId?: string
): PlayView[] {
  const needle = query.search.trim().toLowerCase();
  const active = Object.entries(query.filters)
    .filter(([id]) => id !== skipFacetId)
    .map(([id, filter]) => ({ facet: facets.get(id)?.facet, filter }))
    .filter(
      (entry): entry is { facet: FilterFacet; filter: FacetFilter } => entry.facet !== undefined
    );
  return plays.filter((play) => {
    if (query.unit !== 'all' && phaseOf(play) !== query.unit) return false;
    if (query.isConflictsOnly && play.conflict_status !== 'unresolved') return false;
    if (needle !== '' && !searchText(play, columns).includes(needle)) return false;
    return active.every(({ facet, filter }) => matchesFacet(play, facet, filter));
  });
}

/** Stable sort by a column; blanks always last; ties by play #. */
export function sortPlays(
  plays: readonly PlayView[],
  query: ArchiveQuery,
  byId: ReadonlyMap<string, ArchiveColumn>
): PlayView[] {
  const column = query.sort === null ? undefined : byId.get(query.sort);
  const direction = query.dir === 'desc' ? -1 : 1;
  return plays.slice().sort((left, right) => {
    if (column === undefined) return left.play_number - right.play_number;
    const a = column.sortValue(left);
    const b = column.sortValue(right);
    if (a === null && b === null) return left.play_number - right.play_number;
    if (a === null) return 1;
    if (b === null) return -1;
    const order =
      typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    return order === 0 ? left.play_number - right.play_number : order * direction;
  });
}

export interface FacetOption extends ValueOption {
  /** Plays that would show if only this value were picked (rest of the query applied). */
  count: number;
}

/**
 * The checklist for an enum facet: the fixed vocabulary plus every value in
 * the event (a team option, a retired value, a free tag), with counts, plus a
 * "Not recorded" entry when some plays are blank. Selected values always stay.
 */
export function facetOptions(
  facet: FilterFacet,
  plays: readonly PlayView[],
  selected: readonly string[]
): FacetOption[] {
  const counts = new Map<string, number>();
  let blanks = 0;
  const canonical = new Map<string, string>();
  for (const play of plays) {
    const values = facet.values?.(play) ?? [];
    if (values.length === 0) blanks += 1;
    for (const value of new Set(values)) {
      const key = value.toLowerCase();
      if (!canonical.has(key)) canonical.set(key, value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const label = facet.optionLabel ?? ((value: string) => value);
  const out: FacetOption[] = [];
  const added = new Set<string>();
  const push = (value: string): void => {
    const key = value.toLowerCase();
    if (added.has(key)) return;
    added.add(key);
    out.push({ value, label: label(value), count: counts.get(key) ?? 0 });
  };
  for (const option of facet.fixedOptions ?? []) push(option.value);
  const discovered = [...canonical.values()].sort((a, b) =>
    label(a).localeCompare(label(b), undefined, { numeric: true, sensitivity: 'base' })
  );
  for (const value of discovered) push(value);
  for (const value of selected) if (value !== BLANK_VALUE) push(value);
  if (blanks > 0 || selected.includes(BLANK_VALUE)) {
    out.push({ value: BLANK_VALUE, label: BLANK_LABEL, count: blanks });
  }
  return out;
}
